import React, { useState, useEffect, useRef } from 'react';
import { X, Trash2, Calendar, FileText, CheckCircle, AlertCircle, ShoppingCart } from 'lucide-react';
import ImpactSummary from '../search/ImpactSummary';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * CartModal - Warehouse-specific checkout modal
 * Expands from the clicked basket with org-grouped items
 * 
 * @param {boolean} isOpen - Modal visibility
 * @param {function} onClose - Close handler
 * @param {string} orgId - Organization UUID
 * @param {string} orgName - Organization display name
 * @param {array} items - Items in this org's basket
 * @param {number} itemCount - Total item count
 * @param {object} basketPosition - {x, y} screen coordinates of clicked basket
 * @param {function} onRemoveItem - Remove item from cart
 * @param {function} onCheckout - Checkout handler (generates payload)
 * @param {string} globalImpactMode - Current impact mode from SearchV2 ('environmental' | 'community' | 'cost' | null)
 */
const CartModal = ({
  isOpen,
  onClose,
  orgId,
  orgName,
  items = [],
  itemCount = 0,
  basketPosition = null,
  onRemoveItem,
  onCheckout,
  globalImpactMode = null,
  userProfile = null,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [preferredPickupDay, setPreferredPickupDay] = useState('');
  const [preferredTimeRange, setPreferredTimeRange] = useState('');
  const [expectedReturnDate, setExpectedReturnDate] = useState(() => {
    const twoWeeks = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    return twoWeeks.toISOString().split('T')[0];
  });
  const [purpose, setPurpose] = useState('');
  const [notes, setNotes] = useState('');
  const [maybeItems, setMaybeItems] = useState(new Set()); // Track items marked as "maybe"
  const modalRef = useRef(null);
  const { t } = useLanguage();
  const checkoutLabel = t('cart.checkout');
  const checkoutButtonLabel = checkoutLabel === 'cart.checkout' ? 'Submit Request' : checkoutLabel;
  
  // Initialize impact mode from global state or random
  const [impactMode, setImpactMode] = useState(() => {
    if (globalImpactMode) {
      return globalImpactMode;
    }
    // Pick random if no global mode set
    const modes = ['environmental', 'community', 'cost'];
    return modes[Math.floor(Math.random() * modes.length)];
  });
  
  // Update impact mode when global mode changes
  useEffect(() => {
    if (globalImpactMode) {
      setImpactMode(globalImpactMode);
    }
  }, [globalImpactMode]);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Animate modal entrance/exit
  useEffect(() => {
    if (isOpen) {
      // First render the modal (hidden)
      setShouldRender(true);
      // Then trigger animation after a brief delay
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // Trigger exit animation
      setIsAnimating(false);
      // Remove from DOM after animation completes
      const timer = setTimeout(() => setShouldRender(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle swipe to close
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd < -100) {
      // Swiped down more than 100px
      onClose();
    }
    setTouchStart(0);
    setTouchEnd(0);
  };

  const handleCheckout = () => {
    // Filter out items marked as "maybe"
    const confirmedItems = items.filter(item => !maybeItems.has(item.propId));
    
    onCheckout({
      orgId,
      orgName,
      items: confirmedItems,
      expectedReturnDate,
      purpose,
      preferredPickupDay,
      preferredTimeRange,
      notes,
    });
  };

  const toggleMaybeItem = (propId) => {
    setMaybeItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(propId)) {
        newSet.delete(propId);
      } else {
        newSet.add(propId);
      }
      return newSet;
    });
  };

  const requestingDisplayName = userProfile?.name || 'Propshare Guest';
  const requestingEmail = userProfile?.email;
  const requestingOrganization = userProfile?.organization || 'Propshare Network';

  if (!shouldRender) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ zIndex: 200 }}
        onClick={onClose}
      />

      {/* Modal - Drawer from bottom */}
      <div
        ref={modalRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`fixed left-0 right-0 bg-white rounded-t-2xl shadow-2xl transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] flex flex-col ${
          isAnimating ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          bottom: 0,
          width: '100%',
          maxWidth: '600px',
          maxHeight: '85vh',
          margin: '0 auto',
          zIndex: 201,
        }}
      >
        {/* Pull Handle */}
        <div 
          className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
          onClick={onClose}
        >
          <div className="w-12 h-1.5 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-4">
            <img 
              src="/images/icons/Basket_Icon.png" 
              alt="Basket"
              className="w-16 h-16 object-contain"
            />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 text-left">{orgName}</h2>
                <p className="text-sm text-gray-600 mt-0.5 text-left">
                  {t('conveyor.itemCount', { count: itemCount })}
                </p>
                <p className="text-xs text-gray-500 text-left">
                  Requesting as {requestingDisplayName}
                  {requestingEmail ? ` (${requestingEmail})` : ''}
                </p>
                <p className="text-xs text-gray-400 text-left">
                  Organization: {requestingOrganization}
                </p>
              </div>
            </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-full transition-colors focus:outline-none focus-visible:ring-0"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Impact Summary - Right under header */}
        {items.length > 0 && (
          <div className="bg-gradient-to-b from-blue-50 to-white border-b">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">{t('cart.impactTitle')}</h3>
                
                {/* Impact Mode Selector */}
                <div className="flex gap-1 bg-white/80 rounded-lg p-1 shadow-sm">
                  <button
                    onClick={() => setImpactMode('environmental')}
                    className={`px-2 py-1 text-[10px] font-medium rounded transition-all ${
                      impactMode === 'environmental'
                        ? 'bg-green-500 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    🌱
                  </button>
                  <button
                    onClick={() => setImpactMode('community')}
                    className={`px-2 py-1 text-[10px] font-medium rounded transition-all ${
                      impactMode === 'community'
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    🤝
                  </button>
                  <button
                    onClick={() => setImpactMode('cost')}
                    className={`px-2 py-1 text-[10px] font-medium rounded transition-all ${
                      impactMode === 'cost'
                        ? 'bg-yellow-500 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    💰
                  </button>
                </div>
              </div>
              
              {/* Impact display - full width stripe */}
              <div className={`-mx-6 px-6 py-3 bg-gradient-to-r ${
                impactMode === 'environmental' ? 'from-green-500 to-emerald-600' :
                impactMode === 'community' ? 'from-blue-500 to-indigo-600' :
                'from-yellow-500 to-amber-600'
              }`}>
                <ImpactSummary
                  selectedItems={items}
                  impactMode={impactMode}
                  totalSelectedCount={itemCount}
                  variant="inline"
                />
              </div>
            </div>
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Items List */}
          <div className="px-6 py-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('cart.itemsToRequest')}</h3>
            
            {items.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">{t('cart.emptyBasket')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item) => {
                  const isMaybe = maybeItems.has(item.propId);
                  return (
                  <div
                    key={item.propId}
                    className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                      isMaybe 
                        ? 'bg-red-100 border border-red-200' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {/* Thumbnail placeholder */}
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.propName}
                            className="w-full h-full object-cover rounded"
                          />
                        ) : (
                          <span className="text-gray-400 text-xs">No img</span>
                        )}
                      </div>
                      
                      {/* Item details */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">
                          {item.propName}
                        </p>
                        <p className="text-xs text-gray-500">
                          Added {new Date(item.addedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2">
                      {/* Maybe toggle */}
                      <button
                        onClick={() => toggleMaybeItem(item.propId)}
                        className={`p-2 rounded-lg transition-colors group focus:outline-none focus-visible:ring-0 ${
                          isMaybe 
                            ? 'bg-yellow-100 hover:bg-yellow-200' 
                            : 'hover:bg-yellow-50'
                        }`}
                        aria-label={isMaybe ? t('cart.confirmed') : t('cart.maybe')}
                        title={isMaybe ? t('cart.confirmed') : t('cart.maybe')}
                      >
                        <span className={`text-base ${
                          isMaybe ? 'opacity-100' : 'opacity-40 group-hover:opacity-60'
                        }`}>
                          🤔
                        </span>
                      </button>

                      {/* Remove button */}
                      <button
                        onClick={() => onRemoveItem(item.propId)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors group focus:outline-none focus-visible:ring-0"
                        aria-label={t('cart.remove')}
                      >
                        <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-600" />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

        {/* Request Details */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {t('cart.requestDetails')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('cart.preferredPickupDay')}
              </label>
              <select
                value={preferredPickupDay}
                onChange={(e) => setPreferredPickupDay(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none bg-white"
              >
                <option value="">{t('cart.pickupDayPlaceholder')}</option>
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('cart.preferredTimeRange')}
              </label>
              <select
                value={preferredTimeRange}
                onChange={(e) => setPreferredTimeRange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none bg-white"
              >
                <option value="">{t('cart.timeRangePlaceholder')}</option>
                <option value="morning">Morning (9am-12pm)</option>
                <option value="afternoon">Afternoon (12pm-5pm)</option>
                <option value="evening">Evening (5pm-8pm)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('cart.expectedReturnDate')}
              </label>
              <input
                type="date"
                value={expectedReturnDate}
                onChange={(e) => setExpectedReturnDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('cart.purpose')}
              </label>
              <input
                type="text"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder={t('cart.purposePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t('cart.notes')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('cart.notesPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none focus:outline-none"
            />
          </div>
        </div>
        </div>

        {/* Footer - Actions */}
        <div className="px-6 py-4 border-t bg-white flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium focus:outline-none focus-visible:ring-0"
          >
            {t('common.cancel')}
          </button>
          
          <button
            onClick={handleCheckout}
            disabled={items.length === 0}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 focus:outline-none focus-visible:ring-0 ${
              items.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            {checkoutButtonLabel}
          </button>
        </div>
      </div>
    </>
  );
};

export default CartModal;
